import React, { createContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getTopic, getTopicComments } from '../api/topics';
import { deleteComment } from '../api/comments';

// components
import CommentCard from '../components/CommentCard';
import ConfirmDialog from '../components/ConfirmDialog';
import CommentModal from '../components/CommentModal';
import TopicPane from '../components/TopicPane';

// mui
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

// types
type CommentListItemType = {
    body: string,
    commentId: number,
    postTime: string,
    userName: string,
    userId: number,
}

type TopicType = {
    body: string,
    openRange: number // 公開範囲(招待:0 / 公開:1)
    postStatus: number, // 投稿ステータス(下書き:0 / 投稿:1)
    postTime: string, // 投稿時間 (yyyy-MM-dd HH:mm:ss)
    roomId: number,
    title: string,
    topicDetailId: number,
    topicId: number,
    topicMembers: {
        userId: number,
        userName: string,
    }[],
    userId: number,
    userName: string,
}

// context
export const TopicContext = createContext<TopicType | undefined>(undefined);

export default function Topic() {
    const params = useParams();
    const topicId = params.topicId as string;

    // コンテクスト：トピックの取得
    const [topic, setTopic] = useState<TopicType>();

    useEffect(() => {
        (async () => {
            const response = await getTopic(topicId);
            if (response) {
                setTopic(response);
            }
        })()
    }, [topicId]);

    // モーダル
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isReply, setIsReply] = useState(false);

    // コメントカード、トピックコメントの取得
    const [commentList, setCommentList] = useState<CommentListItemType[]>([]);

    const fnGetTopicComments = async (topicId: string) => {
        const response = await getTopicComments(topicId);
        if (response) {
            setCommentList(response);
        }
    }

    useEffect(() => {
        if (topicId) {
            fnGetTopicComments(topicId);

            const interval = setInterval(() => {
                fnGetTopicComments(topicId);
            }, 30000);
            return () => clearInterval(interval);
        }
    }, [topicId]); // eslint-disable-line

    // トピックコメントの更新 
    const [targetEditComment, setTargetEditComment] = useState(0); // 編集コメントID
    const [targetEditCommentEditorState, setTargetEditCommentEditorState] = useState<string | undefined>(undefined); // 要改名：setTargetEditCommentContentAsJSON

    useEffect(() => {
        if (!isEdit && !isOpenModal) {
            setTargetEditComment(0);
            setTargetEditCommentEditorState(undefined);
        }
    }, [isEdit, isOpenModal]);

    // コメントの削除
    const [targetDeleteComment, setTargetDeleteComment] = useState(0);
    const fnDeleteComment = async (commentId: string, topicId: string) => {
        const result = await deleteComment(commentId, topicId);
        return result;
    };

    // コメントの返信
    const [targetReplyCommentNodeAsJSON, setTargetReplyCommentNodeAsJSON] = useState<string>("");
    const [targetReplyUser, setTargetReplyUser] = useState<{ userId: number, userName: string }>({ userId: 0, userName: "" });

    useEffect(() => {
        if (!isReply && !isOpenModal) {
            setTargetReplyCommentNodeAsJSON("");
            setTargetReplyUser({ userId: 0, userName: "" });
        }
    }, [isReply, isOpenModal])

    // 削除確認ダイアログ
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
    const handleDeleteCommentAgree = () => {
        fnDeleteComment(targetDeleteComment.toString(), topicId)
            .then((result) => {
                fnGetTopicComments(topicId);
            });
    }
    const handleDeleteCommentDisagree = () => {
        setTargetDeleteComment(0);
    }

    return (
        <TopicContext.Provider value={topic}>
            <TopicPane>
                <List>
                    {commentList.map((item, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                px: 0,
                            }}
                        >
                            <CommentCard
                                body={item.body}
                                commentId={item.commentId}
                                postTime={item.postTime}
                                setIsEdit={setIsEdit}
                                setIsOpenConfirmDialog={setIsOpenConfirmDialog}
                                setIsOpenModal={setIsOpenModal}
                                setIsReply={setIsReply}
                                setTargetEditComment={setTargetEditComment}
                                setTargetEditCommentEditorState={setTargetEditCommentEditorState}
                                setTargetDeleteComment={setTargetDeleteComment}
                                setTargetReplyCommentNodeAsJSON={setTargetReplyCommentNodeAsJSON}
                                setTargetReplyUser={setTargetReplyUser}
                                userName={item.userName}
                                userId={item.userId}
                            />
                        </ListItem>
                    ))}
                </List>
            </TopicPane>

            {/* コメント投稿ボタン */}
            <Button
                onClick={() => setIsOpenModal(true)}
                sx={{
                    position: "fixed",
                    right: 16,
                    bottom: 16,
                    zIndex: 100,
                }}
                variant="contained"
            >
                コメント投稿
            </Button>

            {/* コメント投稿モーダル */}
            <CommentModal
                fnGetTopicComments={fnGetTopicComments}
                isEdit={isEdit}
                isOpen={isOpenModal}
                isReply={isReply}
                setIsEdit={setIsEdit}
                setIsOpen={setIsOpenModal}
                setIsReply={setIsReply}
                targetEditComment={targetEditComment}
                targetEditCommentEditorState={targetEditCommentEditorState}
                targetReplyCommentNodeAsJSON={targetReplyCommentNodeAsJSON}
                targetReplyUser={targetReplyUser}
            />

            {/* 削除確認ダイアログ */}
            <ConfirmDialog
                agreeButtonDisplay={"削除する"}
                body={<>入力した内容は削除されます。<br/>一度削除すると、元に戻すことはできません。削除しますか？</>}
                handleClickAgree={handleDeleteCommentAgree}
                handleClickDisagree={handleDeleteCommentDisagree}
                isOpen={isOpenConfirmDialog}
                setIsOpen={setIsOpenConfirmDialog}
                title={"コメントを削除しますか？"}
            />
        </TopicContext.Provider>
    );
}